import * as React from 'react';
import { Text } from '../../display';
import { buildOptionsContainerClasses, buildFieldsetClasses } from './groupHelpers';

interface GroupLabelProps {
  label?: string;
  required: boolean;
}

/**
 * Shared label component for CheckboxGroup and RadioGroup fieldsets.
 */
function GroupLabel({ label, required }: Readonly<GroupLabelProps>) {
  if (!label) return null;

  return (
    <Text
      as="label"
      size="sm"
      weight="medium"
      className="mb-2 block text-secondary-700" // neutral-700
    >
      {label}
      {required && (
        <span className="ml-0.5 text-error-700" aria-hidden="true">
          *
        </span>
      )}
    </Text>
  );
}

interface GroupMessageProps {
  message?: string;
  messageId?: string;
  isError: boolean | string | undefined;
}

/**
 * Shared helper/error message component for CheckboxGroup and RadioGroup.
 */
function GroupMessage({ message, messageId, isError }: Readonly<GroupMessageProps>) {
  if (!message) return null;

  return (
    <Text
      id={messageId}
      size="xs"
      color={isError ? 'error' : 'muted'}
      className="mt-2"
    >
      {message}
    </Text>
  );
}

// InputLabel -- shared label for non-group inputs
interface InputLabelProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  className?: string;
}

function InputLabel({ label, htmlFor, required, className = 'mb-1.5' }: Readonly<InputLabelProps>) {
  return (
    <Text
      as="label"
      htmlFor={htmlFor}
      size="sm"
      weight="medium"
      className={`${className} block text-secondary-700`}
    >
      {label}
      {required && (
        <span className="ml-0.5 text-error-700" aria-hidden="true">
          *
        </span>
      )}
    </Text>
  );
}

// InputMessage -- shared helper/error message for non-group inputs
interface InputMessageProps {
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  helperId?: string;
}

function InputMessage({ error, errorMessage, helperText, helperId }: Readonly<InputMessageProps>) {
  const showError = error && errorMessage;
  const message = showError ? errorMessage : helperText;

  if (!message) return null;

  return (
    <Text
      /* v8 ignore start */
      id={message ? helperId : undefined}
      /* v8 ignore stop */
      size="xs"
      color={showError ? 'error' : 'muted'}
      className="mt-1.5"
    >
      {message}
    </Text>
  );
}

// GroupFieldset -- shared fieldset wrapper for CheckboxGroup and RadioGroup
interface GroupFieldsetProps {
  groupId: string;
  className: string;
  messageId: string | undefined;
  error: boolean;
  label?: string;
  required: boolean;
  orientation: 'horizontal' | 'vertical';
  message: string | undefined;
  showError: string | false | undefined;
  /** Extra props spread onto the options container div (e.g. role, aria-labelledby) */
  optionsContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  children: React.ReactNode;
}

const GroupFieldset = React.forwardRef<HTMLFieldSetElement, GroupFieldsetProps>(
  (
    {
      groupId, className, messageId, error,
      label, required, orientation,
      message, showError,
      optionsContainerProps,
      children,
    },
    ref
  ) => {
    const optionsContainerClasses = buildOptionsContainerClasses(orientation);
    const fieldsetClasses = buildFieldsetClasses(className);

    return (
      <fieldset
        ref={ref}
        id={groupId}
        className={fieldsetClasses}
        aria-describedby={messageId}
        aria-invalid={error}
      >
        <GroupLabel label={label} required={required} />

        {/* Options */}
        <div className={optionsContainerClasses} {...optionsContainerProps}>
          {children}
        </div>

        <GroupMessage message={message} messageId={messageId} isError={showError} />
      </fieldset>
    );
  }
);

GroupFieldset.displayName = 'GroupFieldset';

export { GroupLabel, GroupMessage, InputLabel, InputMessage, GroupFieldset };
